/**
 * Domínio do mapa (M2) — pins e agrupamento de instrumentos.
 * TS puro, testável sem mock. Regra de arquitetura: sem React/Supabase/DOM.
 */
import type { Instrument, InstrumentType, Reading, TarpLevel } from '@/domain';
import {
  FORA_DE_SERVICO, NIVEIS_EXIBIDOS, nivelExibidoDeInstrumento, SEM_FAIXA, SEM_LEITURA,
  SEM_SINAL,
  type NivelExibido,
} from '@/domain/instrumentos/nivel-exibido';
import { utmToWgs84 } from './coords';

/**
 * Cor do pin por nível (paleta do protótipo v5) + o neutro de "sem leitura"
 * (GUPM-228). Espelha `NIVEL_EXIBIDO_UI` de `src/ui/tokens` — duplicado porque
 * este arquivo é domínio do mapa e não pode importar da camada de UI.
 */
export const LEVEL_COLOR: Record<NivelExibido, string> = {
  NORMAL: '#22c55e',
  ATENCAO: '#eab308',
  ALERTA: '#f97316',
  EMERGENCIA: '#ef4444',
  [SEM_LEITURA]: '#cbd5e1',
  // GUPM-247 — sem faixa de controle. Mais escuro que o "sem leitura" de
  // propósito, e no pino ele vem vazado (ver `MapPin.outline`).
  [SEM_FAIXA]: '#94a3b8',
  // GUPM-303 — sem sinal usa o laranja do ALERTA, e não uma cor própria: o catálogo
  // declara `TARP-PS-TILT` com `nivel: 'ALERTA'`. Precisa ser IDÊNTICO ao token de
  // `src/ui/tokens.ts` — esta tabela é uma cópia (o domínio do mapa não importa da
  // camada de UI), e as duas divergindo pintam o mesmo instrumento de cores
  // diferentes no mapa e na tabela. Há teste afirmando a igualdade.
  [SEM_SINAL]: '#f97316',
  // GUPM-307 — cinza `jf-muted`, o mesmo do SEM_FAIXA: os dois sao "a tela nao afirma
  // nivel aqui". Copia de `src/ui/tokens.ts`, com teste afirmando a igualdade.
  [FORA_DE_SERVICO]: '#94a3b8',
};

// GUPM-134 — o catálogo de tipos (símbolo/rótulo/ordem) migrou para o domínio
// compartilhado (`src/domain/instrumentos/instrument-type`), reusado pela tabela
// do design system. Reexportado aqui com os nomes históricos usados pelo mapa.
import {
  INSTRUMENT_TYPE_META,
  INSTRUMENT_TYPE_ORDER,
  type InstrumentTypeMeta,
} from '@/domain/instrumentos/instrument-type';

export type TypeMarker = InstrumentTypeMeta;
export const TYPE_MARKER = INSTRUMENT_TYPE_META;
export const TYPE_ORDER = INSTRUMENT_TYPE_ORDER;

/**
 * Ordem dos 4 níveis reais. Usada onde o resultado tem de ser um `TarpLevel`
 * (nível geral do Dashboard, semáforo) — **não** inclui "sem leitura".
 */
export const LEVEL_ORDER: TarpLevel[] = ['NORMAL', 'ATENCAO', 'ALERTA', 'EMERGENCIA'];

/**
 * Ordem da legenda e do filtro do mapa — GUPM-228. Alias do domínio: era uma cópia
 * própria e o GUPM-303 provou o custo disso (a cópia gêmea do Resumo Técnico ficou sem
 * o `SEM_SINAL`). Todo estado precisa estar aqui, senão ele some do mapa ao aplicar o
 * filtro de níveis — nenhum dos 4 medidos casa com ele.
 */
export const NIVEL_EXIBIDO_ORDER: readonly NivelExibido[] = NIVEIS_EXIBIDOS;

/**
 * Nível atual de um instrumento = leitura mais recente × existência de faixa.
 *
 * GUPM-228 — sem leitura devolve `SEM_LEITURA`, não NORMAL: pino verde afirmaria
 * "medido e dentro da faixa" para um instrumento que ninguém mediu.
 * GUPM-247 — sem faixa devolve `SEM_FAIXA`: mediu, mas não há régua para comparar.
 *
 * Recebe o instrumento inteiro (não só o id) porque a existência de carta mora
 * nele — `thresholdsSource`.
 */
export function resolveLevel(
  // GUPM-303 — `type` e `isOnline` entraram no Pick: o frescor faz parte de "que cor
  // este instrumento tem", e sem eles o pino do mapa ficaria verde para tiltimetro mudo.
  // GUPM-307 — `foraDeServicoDesde` pelo mesmo motivo: sem ele o pino pinta EMERGENCIA a
  // partir da leitura congelada de um instrumento que ja saiu de campo.
  instrument: Pick<
    Instrument,
    'id' | 'type' | 'isOnline' | 'foraDeServicoDesde' | 'thresholdsSource' | 'canaisComFaixa'
  >,
  readings: Reading[],
): NivelExibido {
  // GUPM-270 — delega à forma única do domínio. Este branch já morou aqui, foi copiado
  // para outros pontos de render e esquecido em dois deles; manter uma segunda cópia é
  // convidar a divergência de volta.
  return nivelExibidoDeInstrumento(instrument, readings);
}

/** Coordenada UTM válida = ambos presentes, finitos e não-zero. */
function hasValidUtm(
  inst: Instrument,
): inst is Instrument & { easting: number; northing: number } {
  return inst.easting !== null && inst.northing !== null
    && Number.isFinite(inst.easting) && Number.isFinite(inst.northing)
    && inst.easting !== 0 && inst.northing !== 0;
}

export interface MapPin {
  id: string;
  code: string;
  type: InstrumentType;
  label: string;
  level: NivelExibido;
  color: string;
  /** GUPM-247 — pino vazado (sem preenchimento): estado sem lastro de faixa. */
  outline: boolean;
  symbol: string;
  lat: number;
  lon: number;
}

/**
 * Constrói os pins do mapa a partir dos instrumentos e suas leituras mais recentes.
 * - Nível = leitura mais recente do instrumento (NORMAL se não houver leitura).
 * - Instrumentos sem coordenada UTM válida são omitidos.
 *
 * GUPM-130 — gate revisitado: na GUPM-129, instrumento v2 (`gpxArea !== null`) era
 * sempre omitido porque a única coord disponível para ele era MOCK (herdada do seed
 * em colisões de código). Agora o datasource sempre entrega `Instrument.easting`/
 * `Instrument.northing` já resolvidos pra fonte real quando existe par de mapa; quem
 * não tem par chega com `easting/northing = null`. Ou seja, toda coord que chega aqui
 * já é de fonte confiável — o gate passa a ser só "tem coord UTM válida"
 * (`hasValidUtm`), independente de `gpxArea`.
 */
export function buildMapPins(instruments: Instrument[], readings: Reading[]): MapPin[] {
  const pins: MapPin[] = [];
  for (const inst of instruments) {
    if (!hasValidUtm(inst)) continue;
    const level = resolveLevel(inst, readings);
    const [lat, lon] = utmToWgs84(inst.easting, inst.northing);
    pins.push({
      id: inst.id,
      code: inst.code,
      type: inst.type,
      label: inst.label,
      level,
      color: LEVEL_COLOR[level],
      // GUPM-303 — o SEM_SINAL entrou aqui junto com a cor. Ele COMPARTILHA o laranja
      // do ALERTA, então sem o vazado os dois viram o mesmo pino: "mediu alerta" e
      // "parou de medir" ficariam indistinguíveis no mapa. Distinção por forma, não só
      // por matiz (WCAG 1.4.1) — o comentário do `LEVEL_COLOR` já prometia isso desde
      // o commit anterior, mas a condição aqui só olhava o SEM_FAIXA.
      outline: level === SEM_FAIXA || level === SEM_SINAL,
      symbol: TYPE_MARKER[inst.type].symbol,
      lat,
      lon,
    });
  }
  return pins;
}

/**
 * GUPM-227 — códigos dos instrumentos ATIVOS que não têm pino, ordenados.
 *
 * Substituiu o `nenhumInstrumentoPinavel` do GUPM-129/130, que devolvia um booleano
 * e só acusava o caso extremo (ZERO pinos na barragem). Com fonte única GPX v2 o caso
 * normal passou a ser parcial — 6 ativos sem nenhuma coordenada, porque só aparecem na
 * rota de monitoramento e o inventário não os tem, mais os que o inventário mandou com
 * coordenada irrecuperável. O booleano ficava calado nesse cenário: o instrumento
 * aparecia na lista, não aparecia no mapa, e nada explicava a diferença.
 *
 * Avaliar sobre o dataset da barragem, **não** sobre o filtro da legenda — senão
 * desmarcar um tipo viraria "pendência de coordenada".
 *
 * **Retirado fica fora**: não ter pino é o comportamento esperado dele, não pendência.
 */
export function instrumentosSemPino(instruments: Instrument[]): string[] {
  return instruments
    .filter((inst) => inst.isActive && !hasValidUtm(inst))
    .map((inst) => inst.code)
    .sort((a, b) => a.localeCompare(b, 'pt-BR'));
}

/**
 * Filtra instrumentos pelos conjuntos ativos de tipo e nível (legenda-filtro).
 * Um instrumento passa se seu tipo está em `activeTypes` E o nível da leitura
 * mais recente está em `activeLevels`. STUB (TDD red).
 */
export function filterInstruments(
  instruments: Instrument[],
  readings: Reading[],
  activeLevels: ReadonlySet<NivelExibido>,
  activeTypes: ReadonlySet<InstrumentType>,
): Instrument[] {
  return instruments.filter(
    (inst) => activeTypes.has(inst.type) && activeLevels.has(resolveLevel(inst, readings)),
  );
}

export interface InstrumentGroup {
  type: InstrumentType;
  label: string;
  items: Instrument[];
}

/** Agrupa instrumentos por tipo na ordem de exibição da tabela (só tipos presentes). */
export function groupInstrumentsByType(instruments: Instrument[]): InstrumentGroup[] {
  const groups: InstrumentGroup[] = [];
  for (const type of TYPE_ORDER) {
    const items = instruments.filter((i) => i.type === type);
    if (items.length > 0) {
      groups.push({ type, label: TYPE_MARKER[type].label, items });
    }
  }
  return groups;
}
